import Big from 'big.js';
import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { GetQuotes, OpenQuoteInvoice } from 'smg/customer-api/v2/quotes';
import { ShowError } from 'smg/errors';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import * as models from 'smg/customer-api/models';
import * as session from 'smg/customer-api/session';
import * as html from 'smg/html';
import { Picture } from 'smg/picture';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import { BindClearSearch, PageLoader } from '../account';
import * as carts from '../carts';
import { Relref } from '../hugo';
import { OrganizationView, RenderOrganizationSwitch, ResolveOrganizationView } from '../organization-view';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const $empty = document.querySelector('#quotes-empty') as HTMLElement;
    const $emptyTitle = document.querySelector('#quotes-empty-title') as HTMLElement;
    const $emptyBody = document.querySelector('#quotes-empty-body') as HTMLElement;
    const $emptyCta = document.querySelector('#quotes-empty-cta') as HTMLElement;
    const $noMatch = document.querySelector('#quotes-no-match') as HTMLElement;
    const $noMatchTitle = document.querySelector('#quotes-no-match-title') as HTMLElement;
    const $noMatchBody = document.querySelector('#quotes-no-match-body') as HTMLElement;

    BindClearSearch();

    let view: OrganizationView;
    let lineItems: Map<string, models.LineItem>;

    const pl: PageLoader<models.Quote> = new PageLoader<models.Quote>({
        cards: { empty: $empty, noMatch: $noMatch },
        init: async () => {
            const user = await session.Session().then(s => s.user);
            view = await ResolveOrganizationView(user);
            RenderOrganizationSwitch(view, user);

            lineItems = await GetLineItems({ data: 'name,pluralName,product' });
        },
        get: () => GetQuotes({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key,userID',
            p: pl.currentPage(),
            q: pl.search(),
            ...view.params(),
        }).then(data => ({ rows: data.results, nextPage: data.nextPage })),
        draw: quote => {
            const el = template.Clone('#quote-template').firstElementChild as HTMLElement;

            const serial = el.querySelector('.quote-serial') as HTMLElement;
            serial.textContent = String(quote.serial);

            const date = el.querySelector('.quote-date') as HTMLElement;
            date.textContent = quote.created?.isValid ? quote.created.toLocaleString(DateTime.DATE_SHORT) : '';

            if (view.active) {
                const $attributionRow = el.querySelector('.quote-attribution-row') as HTMLElement;
                const $attribution = el.querySelector('.organization-attribution') as HTMLElement;
                view.fillAttribution($attribution, quote.userID);
                $attributionRow.classList.remove('d-none');
            }

            const total = Big(quote.cachedTotal || 0);
            const shipping = Big(0);

            const subtotal = total.sub(shipping);

            const subtotalEls = el.querySelectorAll('.quote-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.quote-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const totalEls = el.querySelectorAll('.quote-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            const addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
            addToCart.addEventListener('click', async () => {
                buttons.Start(addToCart);
                await carts.AddCartItem({ quoteID: quote.id ?? '' }, view.cartOptions()).then(added => {
                    if (!added) {
                        return;
                    }
                    addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                    addToCart.classList.add('btn-outline-success');

                    addToCart.querySelector('i')?.classList.remove('bi-cart-fill');
                    addToCart.querySelector('i')?.classList.add('bi-check-lg');
                }).finally(() => buttons.Stop(addToCart));
            });

            const invoice = el.querySelector('.quote-invoice') as HTMLButtonElement;
            invoice.addEventListener('click', () => {
                buttons.Start(invoice);
                void OpenQuoteInvoice(`id:${quote.id ?? ''}`, view.params().organization).catch(ShowError).finally(() => buttons.Stop(invoice));
            });

            const proofsContainer = el.querySelector('.quote-proofs-container') as HTMLElement;
            const proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;
            if (quote.quoteProofs.length) {
                for (const op of quote.quoteProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = p.getURLs();

                    const a = template.Clone('#quote-proof-template').firstElementChild as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', quote.id ?? '');

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.alt = op.name || '';
                    if (urls.main) {
                        void Picture(img, urls.main, {
                            displayWidth: 120,
                            alt: op.name || '',
                            fancyboxThumb: true,
                            pdfSrc: p.url() || undefined,
                        });
                    } else {
                        img.src = urls.thumbnail;
                    }

                    const $proofName = a.querySelector('.proof-name') as HTMLElement | null;
                    if (op.name) {
                        if ($proofName) {
                            $proofName.textContent = op.name;
                            $proofName.title = op.name;
                        }
                    } else {
                        $proofName?.remove();
                    }

                    proofsParent.appendChild(a);
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
            }

            if (quote.quoteLineItems.length) {
                const qtys: Record<string, number> = {};
                for (const quoteLineItem of quote.quoteLineItems) {
                    if (!quoteLineItem.quantity) {
                        continue;
                    }

                    const lineItem = lineItems.get(quoteLineItem.lineItemID ?? '');

                    if (!(quoteLineItem.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = quoteLineItem.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + quoteLineItem.quantity;
                }

                const summary = el.querySelector('.quote-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const orderHistoryContainer = el.querySelector('.quote-order-history-container') as HTMLElement;
            if (quote.quoteOrders.length) {
                const orderHistory = el.querySelector('.quote-order-history') as HTMLElement;
                for (const quoteOrder of quote.quoteOrders) {
                    const order = quoteOrder.order;
                    const orderDate = order?.created.isValid ? order.created.toLocaleString(DateTime.DATE_SHORT) : '';
                    const li = html.FromString(`<li>
                        <a href="${view.withLens(`${Relref('/orders')}?q=${order?.invoiceNumber ?? 0}`)}">#${order?.invoiceNumber ?? 0}</a> - ${orderDate}
                    </li>`).firstElementChild as HTMLLIElement;
                    orderHistory.appendChild(li);
                }
            } else {
                orderHistoryContainer.parentElement?.removeChild(orderHistoryContainer);
            }

            const projectContainer = el.querySelector('.quote-project-container') as HTMLElement;
            if (quote.quoteRequest?.quoteRequestProject?.name) {
                const project = el.querySelector('.quote-project') as HTMLElement;
                project.textContent = quote.quoteRequest.quoteRequestProject.name;
            } else {
                projectContainer.parentElement?.removeChild(projectContainer);
            }

            const quoteLink = el.querySelector('.quote-link') as HTMLAnchorElement | null;
            if (quoteLink) {
                quoteLink.href = `${Relref('/quote')}?id=${quote.id ?? ''}`;
            }

            // A revision request acts as the quote's owner (the link carries the
            // owner's bearer key, which the list only emits for their own rows),
            // so a colleague viewing the org gets no such button.
            const revisionLink = el.querySelector('.quote-request-revision') as HTMLAnchorElement;
            if (view.active && !view.isOwn(quote.userID)) {
                revisionLink.remove();
            } else {
                revisionLink.href = `${Relref('/revisions')}?id=${quote.key}`;
            }

            return el;
        },
        afterLoad: state => {
            // A failed init settles before `view` exists, so read it only
            // in the states a resolved view reaches.
            if (state === 'empty' && view.active) {
                const orgName = view.organization?.name ?? '';
                $emptyTitle.textContent = `No quotes for ${orgName} yet`;
                $emptyBody.textContent = 'This list fills in as members receive quotes.';
                $emptyCta.classList.add('d-none');
                return;
            }

            if (state === 'no-match') {
                const orgName = view.organization?.name ?? '';
                const search = pl.search().trim();
                if (view.active) {
                    $noMatchTitle.textContent = `Nothing in ${orgName} matches "${search}"`;
                    $noMatchBody.textContent = `Clear the search to see every quote in ${orgName}.`;
                } else {
                    $noMatchTitle.textContent = `No quotes match "${search}".`;
                }
            }
        },
    });

    pl.load();
}
